import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  TextField,
  ToggleButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CallSplit,
  DataObject,
  FormatAlignCenter,
  FormatAlignJustify,
  FormatAlignLeft,
  FormatAlignRight,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  InsertPageBreak,
  Redo,
  Tune,
  Undo,
} from '@mui/icons-material';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useArnoSelectorCatalog } from '../../hooks/reactQuerys/ArnoAdminQueries';
import {
  DataFieldNode,
  DinoParagraph,
  InlineChoiceNode,
  LabelSource,
  OpenSpotNode,
  RunStyleMark,
} from './docEditorExtensions';
import { fieldLabelResolver } from './fieldPathLabel';
import { InlineChoiceDialog } from './InlineChoiceDialog';
import {
  DocNode,
  docToParagraphs,
  ParagraphFormat,
  paragraphsToDoc,
} from './richDoc';
import {
  Alignment,
  ALIGNMENT_LABEL,
  EmptyBehavior,
  EMPTY_BEHAVIOR_LABEL,
  formatTabs,
  IInlineChoice,
  IndentPreset,
  INDENT_LABEL,
  IRichParagraph,
  lineSpacingChoice,
  LINE_SPACING_PRESETS,
  ListKind,
  LIST_LABEL,
  ParagraphStyle,
  PARAGRAPH_STYLE_LABEL,
  parseTabs,
} from './richTextModel';
import { SelectorPicker } from './SelectorPicker';

/**
 * Texteditor für Urkundentext (#1245, Teil 48): EIN Bearbeitungsfeld je Element, wie bei den
 * Systemmails — nicht mehr ein Kasten mit eigener Werkzeugleiste je Absatz.
 *
 * Eine Werkzeugleiste oben wirkt auf den Absatz, in dem der Cursor steht (bei einer Markierung
 * auf alle markierten), wie in Word: fett, kursiv, unterstrichen, Absatztyp, Liste, Ausrichtung,
 * Datenfeld, bedingter Text, Seitenumbruch. Die selteneren Formate — Einzug, Tabstopps,
 * Abstände, Zeilenabstand, Zusammenhalt, Verhalten bei leeren Daten — stehen hinter
 * »Absatzformat …«. Was ein Klon davon mitbringt, bleibt erhalten.
 *
 * Gespeichert wird nie HTML, sondern `IRichParagraph[]` (`richDoc.ts`).
 */

interface Props {
  paragraphs: IRichParagraph[];
  onChange: (paragraphs: IRichParagraph[]) => void;
  disabled?: boolean;
  /** Fragen des Baums — erst damit lässt sich bedingter Text im Satz anlegen. */
  questions?: { questionId: string; prompt: string; answerType: string }[];
  /** Vorschauvorgang für Feldauswahl und Bedingungen. */
  idLegalTransaction?: number | null;
  /** In der Leseansicht: ohne Rahmen, in der Schrift der Urkunde, Cursor gleich im Text. */
  variant?: 'panel' | 'inline';
  autoFocus?: boolean;
  /** Wo der Cursor beim Öffnen steht: Absatz und Zeichenstelle (sonst am Ende). */
  initialCursor?: { paragraph?: number; offset?: number };
  /**
   * Datenfelder einfügen. In einer fertig gerechneten Urkunde (»In DiNo erstellen«) gibt es nichts
   * mehr aufzulösen — dort fehlt der Knopf.
   */
  allowFields?: boolean;
}

const ALIGNMENT_ICON: Record<Alignment, React.ReactNode> = {
  left: <FormatAlignLeft fontSize='small' />,
  center: <FormatAlignCenter fontSize='small' />,
  right: <FormatAlignRight fontSize='small' />,
  both: <FormatAlignJustify fontSize='small' />,
};

const chip = {
  px: 0.5,
  mx: 0.125,
  borderRadius: 0.5,
  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
  fontSize: '0.9em',
  cursor: 'pointer',
  whiteSpace: 'normal',
};

const editorSx = (variant: 'panel' | 'inline') => ({
  '& .ProseMirror': {
    outline: 'none',
    minHeight: 48,
    px: variant === 'panel' ? 1.5 : 0,
    py: 1,
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontSize: '1rem',
    lineHeight: 1.7,
    counterReset: 'dino-list',
  },
  '& .ProseMirror p': { m: 0, mb: 1, position: 'relative' },
  // Leere Absätze (im Altbestand zwischen fast jeder Zeile) schmal wie in der Leseansicht.
  '& .ProseMirror p:has(> br.ProseMirror-trailingBreak:only-child)': {
    mb: 0,
    lineHeight: 0.8,
    minHeight: '0.8em',
  },
  '& .ProseMirror p:not([data-list])': { counterReset: 'dino-list' },
  '& .ProseMirror p[data-list]': { pl: 3 },
  '& .ProseMirror p[data-list]::before': {
    position: 'absolute',
    left: 0,
    color: 'text.secondary',
  },
  '& .ProseMirror p[data-list="bullet"]::before': { content: '"•"' },
  '& .ProseMirror p[data-list="decimal"]': { counterIncrement: 'dino-list' },
  '& .ProseMirror p[data-list="decimal"]::before': {
    content: 'counter(dino-list) "."',
  },
  '& .ProseMirror p[data-list="lowerLetter"]': {
    counterIncrement: 'dino-list',
  },
  '& .ProseMirror p[data-list="lowerLetter"]::before': {
    content: 'counter(dino-list, lower-alpha) ")"',
  },
  // Word beginnt hier eine neue Seite — im Editor eine dezente Linie.
  '& .ProseMirror p[data-page-break]': {
    borderTop: '1px dashed',
    borderColor: 'divider',
    pt: 0.5,
  },
  // Der Satz geht im nächsten Element weiter (Klon mit Bedingung im Satz).
  '& .ProseMirror p[data-continues]::after': {
    content: '" …"',
    color: 'text.disabled',
  },
  '& .dino-field': {
    ...chip,
    bgcolor: '#e8f1fb',
    borderBottom: '1px dotted #3f7cac',
  },
  '& .dino-choice': {
    ...chip,
    bgcolor: '#fdf1e2',
    borderBottom: '1px dotted #b0763a',
  },
  '& .dino-migration': {
    ...chip,
    color: 'error.main',
    border: '1px dashed',
    borderColor: 'error.main',
  },
  '& .ProseMirror-selectednode': {
    outline: '2px solid',
    outlineColor: 'primary.main',
  },
});

type FieldEdit = { pos: number | null; selector: string };

/** Position im Editor-Dokument für »Absatz n, Zeichen k« — begrenzt auf den Absatz. */
const cursorPosition = (
  doc: {
    childCount: number;
    child: (_index: number) => { nodeSize: number; content: { size: number } };
  },
  cursor?: { paragraph?: number; offset?: number }
): number | null => {
  if (cursor?.paragraph === undefined || cursor.paragraph >= doc.childCount) {
    return null;
  }
  let position = 0;
  for (let index = 0; index < cursor.paragraph; index += 1) {
    position += doc.child(index).nodeSize;
  }
  const paragraph = doc.child(cursor.paragraph);
  return position + 1 + Math.min(cursor.offset ?? 0, paragraph.content.size);
};

const normalText = (paragraphs: IRichParagraph[]): string =>
  JSON.stringify(docToParagraphs(paragraphsToDoc(paragraphs)));
type ChoiceEdit = { pos: number | null; value: IInlineChoice | null };

export const DocumentTextEditor: React.FC<Props> = ({
  paragraphs,
  onChange,
  disabled,
  questions,
  idLegalTransaction,
  variant = 'panel',
  autoFocus,
  initialCursor,
  allowFields = true,
}) => {
  const { selectorCatalog } = useArnoSelectorCatalog(
    idLegalTransaction ?? null
  );
  const fieldLabel = useMemo(
    () => fieldLabelResolver(selectorCatalog),
    [selectorCatalog]
  );
  // Stabil über die Lebensdauer des Editors; der Katalog kann später kommen.
  const labels = useMemo<LabelSource>(
    () => ({ current: (selector, label) => label || selector }),
    []
  );
  labels.current = (selector, label) =>
    fieldLabel(selector) ?? label ?? selector;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Verglichen wird in der Form, die der Editor zurückgibt — sonst gälte schon das Öffnen als
  // Änderung (andere Schlüsselreihenfolge, zusammengefasste Läufe).
  const lastEmitted = useRef(normalText(paragraphs));
  const fieldButton = useRef<HTMLButtonElement | null>(null);
  const [field, setField] = useState<FieldEdit | null>(null);
  const [choice, setChoice] = useState<ChoiceEdit | null>(null);
  const [formatOpen, setFormatOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: false,
        heading: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        listKeymap: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        strike: false,
        link: false,
        dropcursor: false,
        gapcursor: false,
        trailingNode: false,
      }),
      DinoParagraph,
      RunStyleMark,
      DataFieldNode.configure({ labels }),
      InlineChoiceNode,
      OpenSpotNode,
    ],
    content: paragraphsToDoc(paragraphs),
    editable: !disabled,
    autofocus: false,
    onUpdate: ({ editor: current, transaction }) => {
      if (transaction.getMeta('dinoLabels')) {
        return;
      }
      const next = docToParagraphs(current.getJSON() as DocNode);
      const text = JSON.stringify(next);
      if (text === lastEmitted.current) {
        return;
      }
      lastEmitted.current = text;
      onChangeRef.current(next);
    },
    editorProps: {
      handleClickOn: (_view, pos, node) => {
        if (node.type.name === 'inlineChoice') {
          setChoice({ pos, value: node.attrs.choice as IInlineChoice });
          return true;
        }
        if (node.type.name === 'dataField') {
          setField({ pos, selector: String(node.attrs.selector ?? '') });
          return true;
        }
        return false;
      },
    },
  });

  // Von außen geänderter Inhalt (anderes Element, Verschieben) — nicht das eigene Echo.
  useEffect(() => {
    if (!editor) {
      return;
    }
    const text = normalText(paragraphs);
    if (text === lastEmitted.current) {
      return;
    }
    lastEmitted.current = text;
    editor.commands.setContent(paragraphsToDoc(paragraphs), {
      emitUpdate: false,
    });
  }, [editor, paragraphs]);

  // Cursor dorthin, wo geklickt wurde. Das Feld hängt schon im Dokument, wenn dieser Effekt läuft
  // (EditorContent hängt es beim Einhängen ein). Direkt gesetzt, nicht über `focus()`: das wartet
  // auf den nächsten Frame, und einen Timer drosselt der Browser in einem verdeckten Fenster.
  const cursorAtOpen = useRef(initialCursor);
  useEffect(() => {
    if (!editor || !autoFocus || editor.isDestroyed) {
      return;
    }
    const position =
      cursorPosition(editor.state.doc, cursorAtOpen.current) ??
      editor.state.doc.content.size - 1;
    editor.chain().setTextSelection(position).scrollIntoView().run();
    editor.view.focus();
  }, [editor, autoFocus]);

  useEffect(() => {
    // Ohne `false` meldete Tiptap das Umschalten als Änderung — »ungespeichert« schon beim Öffnen.
    if (editor && editor.isEditable === Boolean(disabled)) {
      editor.setEditable(!disabled, false);
    }
  }, [editor, disabled]);

  // Der Feldkatalog kam an: Datenfelder mit ihrem heutigen Namen zeigen, ohne den Text zu ändern.
  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }
    const { tr } = editor.state;
    let changed = false;
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === 'dataField') {
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          refresh: Number(node.attrs.refresh ?? 0) + 1,
        });
        changed = true;
      }
    });
    if (changed) {
      tr.setMeta('dinoLabels', true);
      tr.setMeta('addToHistory', false);
      editor.view.dispatch(tr);
    }
  }, [editor, fieldLabel]);

  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => {
      if (!current) {
        return null;
      }
      const parent = current.state.selection.$from.parent;
      let hasField = false;
      parent.descendants(node => {
        if (node.type.name === 'dataField') hasField = true;
      });
      return {
        bold: current.isActive('bold'),
        italic: current.isActive('italic'),
        underline: current.isActive('underline'),
        format: (parent.attrs.format ?? {}) as ParagraphFormat,
        hasField,
        canUndo: current.can().undo(),
        canRedo: current.can().redo(),
      };
    },
  });

  /** Absatzformat setzen — für den Absatz am Cursor bzw. alle markierten. */
  const patchParagraphs = (patch: Partial<ParagraphFormat>) => {
    editor
      ?.chain()
      .focus()
      .command(({ tr, state: current }) => {
        const { from, to } = current.selection;
        current.doc.nodesBetween(from, to, (node, pos) => {
          if (node.type.name !== 'paragraph') {
            return true;
          }
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            format: { ...(node.attrs.format ?? {}), ...patch },
          });
          return false;
        });
        return true;
      })
      .run();
  };

  const saveField = (selector: string) => {
    if (!editor || !field || !selector.trim()) {
      return;
    }
    const value = selector.trim();
    const label = fieldLabel(value) ?? value.split('.').pop() ?? value;
    if (field.pos === null) {
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'dataField',
          attrs: { selector: value, label, format: {} },
        })
        .run();
    } else {
      const at = field.pos;
      editor
        .chain()
        .focus()
        .command(({ tr }) => {
          const node = tr.doc.nodeAt(at);
          if (node?.type.name === 'dataField') {
            tr.setNodeMarkup(at, undefined, {
              ...node.attrs,
              selector: value,
              label,
            });
          }
          return true;
        })
        .run();
    }
    setField(null);
  };

  const removeAt = (pos: number, type: string) => {
    editor
      ?.chain()
      .focus()
      .command(({ tr }) => {
        const node = tr.doc.nodeAt(pos);
        if (node?.type.name === type) {
          tr.delete(pos, pos + node.nodeSize);
        }
        return true;
      })
      .run();
  };

  const saveChoice = (value: IInlineChoice) => {
    if (!editor || !choice) {
      return;
    }
    if (choice.pos === null) {
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'inlineChoice',
          attrs: { choice: value, format: {} },
        })
        .run();
    } else {
      const at = choice.pos;
      editor
        .chain()
        .focus()
        .command(({ tr }) => {
          const node = tr.doc.nodeAt(at);
          if (node?.type.name === 'inlineChoice') {
            tr.setNodeMarkup(at, undefined, { ...node.attrs, choice: value });
          }
          return true;
        })
        .run();
    }
    setChoice(null);
  };

  const format = state?.format ?? {};
  const styled = Boolean(format.style);
  const off = disabled || !editor;

  return (
    <Box
      sx={
        variant === 'panel'
          ? { border: 1, borderColor: 'divider', borderRadius: 1 }
          : {
              outline: '2px solid',
              outlineColor: 'primary.light',
              borderRadius: 1,
              px: 1.5,
              py: 0.5,
              bgcolor: 'background.paper',
            }
      }
      data-testid='document-text-editor'
    >
      <Stack
        direction='row'
        alignItems='center'
        sx={{
          gap: 0.5,
          flexWrap: 'wrap',
          px: variant === 'panel' ? 1 : 0,
          py: 0.5,
          borderBottom: 1,
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        }}
      >
        <ToggleButton
          size='small'
          value='bold'
          selected={Boolean(state?.bold)}
          disabled={off}
          aria-label='Fett'
          onMouseDown={event => event.preventDefault()}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          <FormatBold fontSize='small' />
        </ToggleButton>
        <ToggleButton
          size='small'
          value='italic'
          selected={Boolean(state?.italic)}
          disabled={off}
          aria-label='Kursiv'
          onMouseDown={event => event.preventDefault()}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <FormatItalic fontSize='small' />
        </ToggleButton>
        <ToggleButton
          size='small'
          value='underline'
          selected={Boolean(state?.underline)}
          disabled={off}
          aria-label='Unterstrichen'
          onMouseDown={event => event.preventDefault()}
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
        >
          <FormatUnderlined fontSize='small' />
        </ToggleButton>

        <Divider orientation='vertical' flexItem sx={{ mx: 0.5 }} />

        <TextField
          select
          size='small'
          variant='standard'
          value={format.style ?? ''}
          disabled={off}
          onChange={event =>
            patchParagraphs({
              style: (event.target.value || null) as ParagraphStyle | null,
            })
          }
          SelectProps={{ displayEmpty: true }}
          sx={{ minWidth: 150 }}
          inputProps={{ 'aria-label': 'Absatztyp' }}
        >
          <MenuItem value=''>Fließtext</MenuItem>
          {(Object.keys(PARAGRAPH_STYLE_LABEL) as ParagraphStyle[]).map(
            style => (
              <MenuItem key={style} value={style}>
                {PARAGRAPH_STYLE_LABEL[style]}
              </MenuItem>
            )
          )}
        </TextField>

        <TextField
          select
          size='small'
          variant='standard'
          value={format.list ?? ''}
          disabled={off}
          onChange={event =>
            patchParagraphs({
              list: (event.target.value || null) as ListKind | null,
            })
          }
          SelectProps={{ displayEmpty: true }}
          sx={{ minWidth: 120 }}
          inputProps={{ 'aria-label': 'Liste' }}
        >
          <MenuItem value=''>Keine Liste</MenuItem>
          {(Object.keys(LIST_LABEL) as ListKind[]).map(kind => (
            <MenuItem key={kind} value={kind}>
              {LIST_LABEL[kind]}
            </MenuItem>
          ))}
        </TextField>

        <Tooltip
          title={
            styled
              ? 'Bei einem Absatztyp bestimmt die Vorlage die Ausrichtung.'
              : ''
          }
        >
          <Stack direction='row'>
            {(Object.keys(ALIGNMENT_LABEL) as Alignment[]).map(alignment => (
              <ToggleButton
                key={alignment}
                size='small'
                value={alignment}
                selected={!styled && format.alignment === alignment}
                disabled={off || styled}
                aria-label={ALIGNMENT_LABEL[alignment]}
                onMouseDown={event => event.preventDefault()}
                onClick={() =>
                  patchParagraphs({
                    alignment:
                      format.alignment === alignment ? null : alignment,
                  })
                }
                sx={{ border: 0 }}
              >
                {ALIGNMENT_ICON[alignment]}
              </ToggleButton>
            ))}
          </Stack>
        </Tooltip>

        <Divider orientation='vertical' flexItem sx={{ mx: 0.5 }} />

        {allowFields && (
          <Button
            size='small'
            ref={fieldButton}
            startIcon={<DataObject fontSize='small' />}
            disabled={off}
            onMouseDown={event => event.preventDefault()}
            onClick={() => setField({ pos: null, selector: '' })}
          >
            Datenfeld
          </Button>
        )}
        {questions && (
          <Button
            size='small'
            startIcon={<CallSplit fontSize='small' />}
            disabled={off}
            onMouseDown={event => event.preventDefault()}
            onClick={() => setChoice({ pos: null, value: null })}
          >
            Bedingter Text
          </Button>
        )}
        <Tooltip title='Seitenumbruch vor diesem Absatz'>
          <span>
            <IconButton
              size='small'
              color={format.pageBreakBefore ? 'primary' : 'default'}
              disabled={off}
              aria-label='Seitenumbruch vor diesem Absatz'
              onMouseDown={event => event.preventDefault()}
              onClick={() =>
                patchParagraphs({ pageBreakBefore: !format.pageBreakBefore })
              }
            >
              <InsertPageBreak fontSize='small' />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          size='small'
          startIcon={<Tune fontSize='small' />}
          disabled={off}
          onMouseDown={event => event.preventDefault()}
          onClick={() => setFormatOpen(true)}
        >
          Absatzformat …
        </Button>

        <Box sx={{ flexGrow: 1 }} />
        <IconButton
          size='small'
          aria-label='Rückgängig'
          disabled={off || !state?.canUndo}
          onMouseDown={event => event.preventDefault()}
          onClick={() => editor?.chain().focus().undo().run()}
        >
          <Undo fontSize='small' />
        </IconButton>
        <IconButton
          size='small'
          aria-label='Wiederholen'
          disabled={off || !state?.canRedo}
          onMouseDown={event => event.preventDefault()}
          onClick={() => editor?.chain().focus().redo().run()}
        >
          <Redo fontSize='small' />
        </IconButton>
      </Stack>

      <Box sx={editorSx(variant)}>
        <EditorContent editor={editor} />
      </Box>
      {variant === 'panel' && (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: 'block', px: 1.5, pb: 0.5 }}
        >
          Eingabetaste: neuer Absatz · Umschalt+Eingabe: Zeilenumbruch
        </Typography>
      )}

      <Popover
        open={Boolean(field)}
        anchorEl={fieldButton.current}
        onClose={() => setField(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, width: 460 } }}
      >
        {field && (
          <FieldChooser
            initial={field.selector}
            idLegalTransaction={idLegalTransaction ?? null}
            existing={field.pos !== null}
            onSave={saveField}
            onRemove={() => {
              if (field.pos !== null) removeAt(field.pos, 'dataField');
              setField(null);
            }}
          />
        )}
      </Popover>

      <InlineChoiceDialog
        open={Boolean(choice)}
        value={choice?.value ?? null}
        questions={questions ?? []}
        idLegalTransaction={idLegalTransaction}
        onSave={saveChoice}
        onDelete={
          choice?.pos !== null && choice?.pos !== undefined
            ? () => {
                removeAt(choice.pos as number, 'inlineChoice');
                setChoice(null);
              }
            : undefined
        }
        onClose={() => setChoice(null)}
      />

      <ParagraphFormatDialog
        open={formatOpen}
        format={format}
        hasField={Boolean(state?.hasField)}
        onClose={() => setFormatOpen(false)}
        onSave={patch => {
          patchParagraphs(patch);
          setFormatOpen(false);
        }}
      />
    </Box>
  );
};

const FieldChooser: React.FC<{
  initial: string;
  idLegalTransaction: number | null;
  existing: boolean;
  onSave: (_selector: string) => void;
  onRemove: () => void;
}> = ({ initial, idLegalTransaction, existing, onSave, onRemove }) => {
  const [selector, setSelector] = useState(initial);
  return (
    <Stack spacing={1.5}>
      <Typography variant='subtitle2'>
        {existing
          ? 'Datenfeld ändern'
          : 'Datenfeld an der Cursorstelle einfügen'}
      </Typography>
      <SelectorPicker
        value={selector}
        onChange={setSelector}
        idLegalTransaction={idLegalTransaction}
      />
      <Stack direction='row' spacing={1} justifyContent='flex-end'>
        {existing && (
          <Button color='error' onClick={onRemove}>
            Entfernen
          </Button>
        )}
        <Button
          variant='contained'
          disabled={!selector.trim()}
          onClick={() => onSave(selector)}
        >
          {existing ? 'Übernehmen' : 'Einfügen'}
        </Button>
      </Stack>
    </Stack>
  );
};

const FORMAT_KEYS = [
  'indent',
  'tabs',
  'spaceBefore',
  'spaceAfter',
  'lineSpacing',
  'lineRule',
  'keepNext',
  'emptyBehavior',
] as const;

/**
 * Nur, was im Dialog geändert wurde. Sonst bekäme bei einer Markierung über viele Absätze jeder
 * die Formate des ersten — auch ein übernommener Zeilenabstand, den niemand angefasst hat.
 */
export const changedFormat = (
  before: ParagraphFormat,
  after: ParagraphFormat
): Partial<ParagraphFormat> => {
  const patch: Partial<ParagraphFormat> = {};
  const normal = (key: (typeof FORMAT_KEYS)[number], value: unknown) =>
    key === 'tabs'
      ? (value ?? [])
      : key === 'keepNext'
        ? Boolean(value)
        : (value ?? null);
  FORMAT_KEYS.forEach(key => {
    const next = normal(key, after[key]);
    if (JSON.stringify(normal(key, before[key])) !== JSON.stringify(next)) {
      (patch as Record<string, unknown>)[key] = next;
    }
  });
  // Zeilenabstand und seine Regel gehören zusammen.
  if ('lineSpacing' in patch || 'lineRule' in patch) {
    patch.lineSpacing = after.lineSpacing ?? null;
    patch.lineRule = after.lineRule ?? null;
  }
  return patch;
};

/** Die selteneren Absatzformate — für den Absatz am Cursor bzw. alle markierten. */
const ParagraphFormatDialog: React.FC<{
  open: boolean;
  format: ParagraphFormat;
  hasField: boolean;
  onClose: () => void;
  onSave: (_patch: Partial<ParagraphFormat>) => void;
}> = ({ open, format, hasField, onClose, onSave }) => {
  const [draft, setDraft] = useState<ParagraphFormat>(format);
  const [tabs, setTabs] = useState(formatTabs(format.tabs));
  // Nur beim Öffnen übernehmen — sonst überschriebe jede Cursorbewegung die Eingabe.
  const current = useRef(format);
  current.current = format;
  useEffect(() => {
    if (open) {
      setDraft(current.current);
      setTabs(formatTabs(current.current.tabs));
    }
  }, [open]);
  const styled = Boolean(draft.style);
  const spacing = lineSpacingChoice({ ...draft, runs: [] });
  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Absatzformat</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {styled && (
            <Typography variant='body2' color='text.secondary'>
              Bei einem Absatztyp bestimmt die Vorlage Einzug, Tabstopps und
              Abstände. Was hier steht, bleibt gespeichert und wirkt wieder,
              wenn der Absatztyp »Fließtext« ist.
            </Typography>
          )}
          <Stack direction='row' spacing={2}>
            <TextField
              select
              size='small'
              label='Einzug'
              value={draft.indent ?? ''}
              disabled={styled}
              onChange={event =>
                setDraft({
                  ...draft,
                  indent: (event.target.value || null) as IndentPreset | null,
                })
              }
              sx={{ flex: 1 }}
            >
              <MenuItem value=''>Keiner</MenuItem>
              {(Object.keys(INDENT_LABEL) as IndentPreset[]).map(preset => (
                <MenuItem key={preset} value={preset}>
                  {INDENT_LABEL[preset]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size='small'
              label='Tabstopps (cm)'
              helperText='z. B. 2; 5,5'
              value={tabs}
              disabled={styled}
              onChange={event => setTabs(event.target.value)}
              sx={{ flex: 1 }}
            />
          </Stack>
          <Stack direction='row' spacing={2}>
            <TextField
              size='small'
              type='number'
              label='Abstand davor (pt)'
              value={draft.spaceBefore ?? ''}
              disabled={styled}
              onChange={event =>
                setDraft({
                  ...draft,
                  spaceBefore:
                    event.target.value === ''
                      ? null
                      : Number(event.target.value),
                })
              }
              sx={{ flex: 1 }}
            />
            <TextField
              size='small'
              type='number'
              label='Abstand danach (pt)'
              value={draft.spaceAfter ?? ''}
              disabled={styled}
              onChange={event =>
                setDraft({
                  ...draft,
                  spaceAfter:
                    event.target.value === ''
                      ? null
                      : Number(event.target.value),
                })
              }
              sx={{ flex: 1 }}
            />
          </Stack>
          <TextField
            select
            size='small'
            label='Zeilenabstand'
            value={spacing.id}
            disabled={styled}
            onChange={event => {
              const preset = LINE_SPACING_PRESETS.find(
                entry => entry.id === event.target.value
              );
              if (preset) {
                setDraft({
                  ...draft,
                  lineSpacing: preset.lineSpacing,
                  lineRule: preset.lineRule,
                });
              }
            }}
          >
            {LINE_SPACING_PRESETS.map(entry => (
              <MenuItem key={entry.id} value={entry.id}>
                {entry.label}
              </MenuItem>
            ))}
            {spacing.id === 'carried' && (
              <MenuItem value='carried'>{spacing.label}</MenuItem>
            )}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(draft.keepNext)}
                onChange={event =>
                  setDraft({ ...draft, keepNext: event.target.checked })
                }
              />
            }
            label='Mit nächstem Absatz zusammenhalten'
          />
          <Tooltip
            title={
              hasField
                ? ''
                : 'Dieser Absatz enthält kein Datenfeld und kann deshalb nie leer sein.'
            }
          >
            <span>
              <TextField
                select
                size='small'
                fullWidth
                label='Wenn alle Datenfelder leer sind'
                value={
                  draft.emptyBehavior === 'OMIT_IF_ALL_DATA_EMPTY'
                    ? 'OMIT_IF_ALL_DATA_EMPTY'
                    : 'KEEP_TEXT'
                }
                disabled={!hasField}
                onChange={event =>
                  setDraft({
                    ...draft,
                    emptyBehavior:
                      event.target.value === 'OMIT_IF_ALL_DATA_EMPTY'
                        ? 'OMIT_IF_ALL_DATA_EMPTY'
                        : null,
                  })
                }
              >
                {(Object.keys(EMPTY_BEHAVIOR_LABEL) as EmptyBehavior[]).map(
                  behavior => (
                    <MenuItem key={behavior} value={behavior}>
                      {EMPTY_BEHAVIOR_LABEL[behavior]}
                    </MenuItem>
                  )
                )}
              </TextField>
            </span>
          </Tooltip>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant='contained'
          onClick={() =>
            onSave(changedFormat(format, { ...draft, tabs: parseTabs(tabs) }))
          }
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
